import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Spin } from 'antd';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { invalidateOperationAnalysisSubjects } from '@/common/utils/operation-analysis-scope';
import { FEISHU_CONTACT_COLLECTION_TYPE, queryCollections } from '../services/collect';
import {
  confirmOperationEntityPrincipalMatches,
  queryOperationEntityPrincipalMatches,
} from '../services/operations';
import {
  buildRecommendationConfirmation,
  filterOperationEntitiesByRecommendation,
} from '../utils/operation-entity-principals';
import OperationEntityTable from './OperationEntityTable';

const OperationEntityPrincipalManagement = ({
  entities,
  onBatchUpdate,
  onCreate,
  onDelete,
  onEdit,
  organizationId,
}) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [matching, setMatching] = useState(false);
  const [recommendationFilter, setRecommendationFilter] = useState('all');

  const {
    data: collectionResult,
    isError: collectionError,
    isPending: collectionPending,
    refetch: refetchCollection,
  } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () =>
      queryCollections({
        collectionType: FEISHU_CONTACT_COLLECTION_TYPE,
        organizationId,
        pageSize: 1,
      }),
    queryKey: ['feishuContactCollection', organizationId],
  });
  const collection = collectionResult?.data?.[0];
  const collectionId = collection?.id;
  const matchMode = collectionId ? 'rematch' : 'current';
  const {
    data: matchData,
    error: recommendationError,
    isError: recommendationFailed,
    isFetching: recommendationFetching,
    isPending: recommendationPending,
    refetch: refetchRecommendations,
  } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () =>
      queryOperationEntityPrincipalMatches({
        ...(collectionId ? { collectionId } : {}),
        mode: matchMode,
        organizationId,
      }),
    queryKey: ['operationEntityPrincipalMatches', organizationId, collectionId, matchMode],
    retry: false,
  });
  const matchItems = useMemo(() => matchData?.items || [], [matchData?.items]);
  const itemByEntityId = useMemo(
    () => Object.fromEntries(matchItems.map((item) => [item.operationEntityId, item])),
    [matchItems],
  );
  const confirmation = useMemo(
    () =>
      buildRecommendationConfirmation({
        entities,
        items: matchItems,
      }),
    [entities, matchItems],
  );
  const filteredEntities = useMemo(
    () =>
      matching
        ? filterOperationEntitiesByRecommendation(entities, matchItems, recommendationFilter)
        : entities,
    [entities, matchItems, matching, recommendationFilter],
  );

  const invalidateData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['operationEntityPrincipalMatches', organizationId, collectionId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['settings-operation-entities', organizationId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['settings-operation-entity-members', organizationId],
      }),
      invalidateOperationAnalysisSubjects(queryClient, organizationId),
    ]);
  };

  const confirmMutation = useMutation({
    mutationFn: confirmOperationEntityPrincipalMatches,
    onSuccess: async () => {
      message.success('负责人已追加');
      setMatching(false);
      setRecommendationFilter('all');
      await invalidateData();
    },
    onError: (error) => message.error(error.message || '确认推荐失败，请重试'),
  });
  const confirmBatch = () => {
    if (!confirmation.items.length) {
      message.warning('暂无可应用的推荐负责人');
      return;
    }
    confirmMutation.mutate({
      applyMode: 'append',
      collectionId,
      items: confirmation.items,
      scope: 'batch',
      organizationId,
      stateToken: matchData.stateToken,
    });
  };
  const startMatching = async () => {
    const result = await refetchRecommendations();
    if (result.error) {
      return;
    }
    setRecommendationFilter('all');
    setMatching(true);
  };
  let recommendationStatus;
  if (collectionPending || (collection && recommendationPending)) {
    recommendationStatus = <Spin size="small" />;
  } else if (collectionError) {
    recommendationStatus = (
      <Alert
        showIcon
        type="error"
        title="通讯录采集配置加载失败"
        description="经营实体和负责人仍可正常维护。"
        action={<Button onClick={() => refetchCollection()}>重新加载</Button>}
      />
    );
  } else if (!collection) {
    recommendationStatus = (
      <Alert
        showIcon
        type="info"
        title="尚未配置飞书通讯录采集"
        description="经营实体和负责人仍可正常维护；配置并完成采集后可使用飞书联系人推荐。"
      />
    );
  } else if (recommendationFailed) {
    recommendationStatus = (
      <Alert
        showIcon
        type="warning"
        title="飞书联系人推荐暂不可用"
        description={
          recommendationError?.response?.status === 404
            ? '通讯录快照尚未生成，经营实体和负责人仍可正常维护。'
            : '经营实体和负责人仍可正常维护。'
        }
        action={<Button onClick={() => refetchRecommendations()}>重新加载</Button>}
      />
    );
  }

  return (
    <OperationEntityTable
      allEntities={entities}
      entities={filteredEntities}
      notice={recommendationStatus}
      onBatchUpdate={onBatchUpdate}
      onCreate={onCreate}
      onDelete={onDelete}
      onEdit={(entity) =>
        onEdit(entity, {
          collectionId,
          contactTree: matchData?.contactTree || [],
          recommendedContact: itemByEntityId[entity.id]?.recommendedContact || null,
        })
      }
      recommendation={{
        itemByEntityId,
        matching,
        toolbar: {
          applicableCount: confirmation.items.length,
          confirming: confirmMutation.isPending,
          matching,
          onCancelMatching: () => {
            setMatching(false);
            setRecommendationFilter('all');
          },
          onAppendMatching: confirmBatch,
          onRecommendationFilterChange: setRecommendationFilter,
          onStartMatching: startMatching,
          recommendationAvailable: Boolean(collectionId && matchData && !recommendationFailed),
          recommendationFilter,
          recommendationRefreshing: recommendationFetching,
        },
      }}
    />
  );
};

OperationEntityPrincipalManagement.propTypes = {
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      parentId: PropTypes.string,
    }),
  ).isRequired,
  onBatchUpdate: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  organizationId: PropTypes.string.isRequired,
};

export default OperationEntityPrincipalManagement;
